import { CmsOffer } from '../../../../../../../types/cmsProduct';
import { extractGiftRange } from '../../../../../../../utils/helpers';
import { calculateOffersValue } from '../../../../../../../utils/helpers/calculateRewardValue';
import { tr } from '../../../../../../../utils/translations/tr';
import { formatAmount } from '../../../formatAmount';
import { OffersPlText } from '../../../i18n';

const lowestStart = (
  offers: CmsOffer[],
  criterionName: string,
): number | undefined => {
  const starts = offers
    .flatMap((offer) => offer.list_of_gifts ?? [])
    .map((gift) => extractGiftRange(gift.criteria_list, criterionName).start)
    .filter((start): start is number => start !== null);
  return starts.length > 0 ? Math.min(...starts) : undefined;
};

// Shown in place of the total when no offer matches (CMS-2023 §7): which of
// the amount and the tenure fall short of every offer's minimum. The highest
// reward is the product's "up to" sum of each offer's best gift. Undefined
// when neither falls short (e.g. an amount in a gap between bands) — the
// ticket has no variant for that.
export const getReminder = (
  offers: CmsOffer[],
  amount: number,
  tenure: number,
  currency: string,
  text: OffersPlText,
): string | undefined => {
  const minAmount = lowestStart(offers, 'ELIGIBLE_LOAN_RANGE');
  const minTenure = lowestStart(offers, 'ELIGIBLE_TENURE_RANGE');
  const amountFallsShort = minAmount !== undefined && amount < minAmount;
  const tenureFallsShort = minTenure !== undefined && tenure < minTenure;
  const replacements = {
    amount: formatAmount(minAmount ?? 0, currency),
    count: String(minTenure ?? 0),
    value: formatAmount(calculateOffersValue(offers), currency),
  };

  if (amountFallsShort && tenureFallsShort) {
    return tr('reminder_amount_and_tenure', text, replacements);
  }
  if (amountFallsShort) return tr('reminder_amount', text, replacements);
  if (tenureFallsShort) return tr('reminder_tenure', text, replacements);
  return undefined;
};
